import React, { useCallback, useEffect, useState } from 'react';
import { Coffee, Flag, Loader2, Play, Power, RotateCcw, Zap } from 'lucide-react';
import { ControlValue, DrainControl, DrainControlState, steeringService, steerError } from '../../services/steeringService';

export const DRAIN_NOW_DEBOUNCE_MS = 30_000;
const POLL_MS = 15_000;

// Shared across mounts so the drain popover and the HUD screen debounce together.
const lastDrainNow = new Map<string, number>();
export const resetDrainNowDebounce = () => lastDrainNow.clear();

const STATE_LABEL: Record<DrainControlState, string> = {
  'pre-start': 'Not started',
  live: 'Live',
  break: 'On a break',
  wrap: 'Wrapping up',
};
const STATE_DOT: Record<DrainControlState, string> = {
  'pre-start': 'bg-slate-300',
  live: 'bg-primary',
  break: 'bg-amber-500',
  wrap: 'bg-indigo-500',
};

type Action = Exclude<ControlValue, 'phase' | 'drain-now'>;

/** Which buttons make sense in each control state. Start always opens a new epoch, so it stays off while live. */
const ALLOWED: Record<DrainControlState, Action[]> = {
  'pre-start': ['start'],
  live: ['break', 'wrap'],
  break: ['resume', 'wrap'],
  wrap: ['start'],
};

const hhmm = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');

/** Today at HH:MM as ISO; tomorrow if that time has passed. */
const untilIso = (value: string): string | undefined => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!m) return undefined;
  const d = new Date();
  d.setHours(Number(m[1]), Number(m[2]), 0, 0);
  if (d.getTime() <= Date.now()) d.setDate(d.getDate() + 1);
  return d.toISOString();
};

interface Props {
  sessionId: string;
  canEdit: boolean;
  source: 'web' | 'hud';
  /** Drain state from a parent that already polls it; the component polls on its own when absent. */
  drain?: DrainControl | null;
  onChanged?: () => void;
}

/** Start, Break, Resume, Wrap and Drain now for the platform drain (agentic core R1a, WS2). */
const SessionControls: React.FC<Props> = ({ sessionId, canEdit, source, drain: drainProp, onChanged }) => {
  const selfLoad = drainProp === undefined;
  const [own, setOwn] = useState<DrainControl | null>(null);
  const [busy, setBusy] = useState<ControlValue | 'enable' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [until, setUntil] = useState('');
  const [now, setNow] = useState(() => Date.now());
  const drain = selfLoad ? own : drainProp;

  const load = useCallback(async () => {
    try {
      setOwn(await steeringService.getDrain(sessionId));
    } catch {
      /* stays on the last state */
    }
  }, [sessionId]);

  useEffect(() => {
    if (!selfLoad) return;
    load();
    const timer = setInterval(() => { if (!document.hidden) load(); }, POLL_MS);
    return () => clearInterval(timer);
  }, [selfLoad, load]);

  const last = lastDrainNow.get(sessionId) ?? 0;
  const debounced = now - last < DRAIN_NOW_DEBOUNCE_MS;

  // Tick once a second only while the drain-now debounce is counting down.
  useEffect(() => {
    if (!debounced) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [debounced]);

  const refresh = async () => {
    if (selfLoad) await load();
    onChanged?.();
  };

  const send = async (value: ControlValue) => {
    if (busy) return;
    if (value === 'drain-now') {
      if (Date.now() - (lastDrainNow.get(sessionId) ?? 0) < DRAIN_NOW_DEBOUNCE_MS) return;
      lastDrainNow.set(sessionId, Date.now());
      setNow(Date.now());
    }
    setBusy(value);
    setError(null);
    try {
      const at = value === 'break' && until ? untilIso(until) : undefined;
      await steeringService.steer(sessionId, { kind: 'control', value, source, ...(at ? { until: at } : {}) });
      if (value === 'break') setUntil('');
      await refresh();
    } catch (e: any) {
      if (value === 'drain-now') lastDrainNow.delete(sessionId);
      setError(steerError(e, 'Could not send the control'));
    } finally {
      setBusy(null);
    }
  };

  const enable = async () => {
    if (busy) return;
    setBusy('enable');
    setError(null);
    try {
      await steeringService.enableDrain(sessionId);
      await refresh();
    } catch (e: any) {
      setError(steerError(e, 'Could not turn the drain on'));
    } finally {
      setBusy(null);
    }
  };

  if (!drain) return null;

  const state: DrainControlState = drain.control ?? 'pre-start';
  const allowed = ALLOWED[state];
  const blocked = !canEdit || !drain.enabled;
  const queued = debounced || drain.forceNext;

  const btn = (value: Action, label: string, Icon: React.ElementType) => (
    <button
      type="button"
      onClick={() => send(value)}
      disabled={blocked || !allowed.includes(value) || !!busy}
      className="flex items-center justify-center gap-1.5 rounded-lg h-8 px-2.5 border border-slate-200 bg-white text-slate-600 text-xs font-semibold hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white transition-colors"
    >
      {busy === value ? <Loader2 size={13} className="animate-spin" /> : <Icon size={13} />} {label}
    </button>
  );

  return (
    <div className="flex flex-col gap-2" data-testid="session-controls">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-bold text-primary tracking-widest uppercase">Session controls</span>
        <span className="flex items-center gap-1.5 text-xs text-slate-600" data-testid="control-state">
          <span className={`h-1.5 w-1.5 rounded-full ${STATE_DOT[state]}`} />
          {STATE_LABEL[state]}
          {state === 'break' && drain.breakUntil && ` until ${hhmm(drain.breakUntil)}`}
        </span>
      </div>

      {!drain.enabled ? (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-3 py-2.5">
          <p className="text-xs text-slate-500">The transcript drain is off. Start, breaks and wrap work once it is on.</p>
          {canEdit && selfLoad && (
            <button
              type="button"
              onClick={enable}
              disabled={!!busy}
              className="shrink-0 flex items-center gap-1.5 rounded-lg h-8 px-3 bg-primary hover:bg-primary-dark text-white text-xs font-bold disabled:opacity-60 transition-colors"
            >
              {busy === 'enable' ? <Loader2 size={13} className="animate-spin" /> : <Power size={13} />} Turn on
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-1.5">
            {btn('start', 'Start', Play)}
            <div className="flex items-center gap-1">
              {btn('break', 'Break', Coffee)}
              {allowed.includes('break') && !blocked && (
                <label className="flex items-center gap-1 text-[11px] text-slate-400">
                  back at
                  <input
                    type="time"
                    value={until}
                    onChange={(e) => setUntil(e.target.value)}
                    aria-label="Break until"
                    className="h-8 border border-slate-200 rounded-lg px-1.5 text-xs text-slate-600 outline-none focus:ring-2 focus:ring-primary"
                  />
                </label>
              )}
            </div>
            {btn('resume', 'Resume', RotateCcw)}
            {btn('wrap', 'Wrap', Flag)}
            <button
              type="button"
              onClick={() => send('drain-now')}
              disabled={blocked || queued || !!busy}
              title="Update the record now instead of waiting for the next drain"
              className="ml-auto flex items-center justify-center gap-1.5 rounded-lg h-8 px-2.5 border border-primary/30 bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 disabled:border-slate-200 disabled:bg-white disabled:text-slate-400 transition-colors"
            >
              {busy === 'drain-now' ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
              {queued ? 'Queued' : 'Drain now'}
            </button>
          </div>
          {!canEdit && <p className="text-xs text-slate-500">You have read-only access to this session.</p>}
          <p className="text-[11px] text-slate-400">Sets the platform's clock only; your laptop loop keeps its own go and break.</p>
        </>
      )}
      {error && <p className="text-xs text-red-600" role="alert">{error}</p>}
    </div>
  );
};

export default SessionControls;
